import { useState } from "react";
import { dateFieldError, type DateFieldError } from "@/lib/date-instant";
import { useProfileMergeStore } from "@/stores/profile-merge-store";

// Decides whether an in-line merge date field's warning should show, and returns
// the handlers to wire to the input. The warning appears once the user LEAVES
// the field (onBlur) or the bar reveals all errors on a blocked save — never
// mid-keystroke. `validate` is false when this value would not be committed (a
// non-date field, or the side the current choice discards), so no warning nags
// on it. Pairs with <MergeDateWarning error={...} />.
export function useDateFieldWarning(
  value: string,
  validate: boolean,
): { error: DateFieldError | null; onBlur: () => void; onEdit: () => void } {
  const reveal = useProfileMergeStore((s) => s.revealDateErrors);
  const [blurred, setBlurred] = useState(false);
  // While the user is actively typing THIS field, suppress its own warning even
  // when a blocked save has globally revealed errors — otherwise the reveal flag
  // (which never turns off) would nag on every keystroke. Other fields keep
  // showing; only the one being edited is quiet until it is left again.
  const [editing, setEditing] = useState(false);
  const err = validate ? dateFieldError(value) : null;
  return {
    error: err && !editing && (blurred || reveal) ? err : null,
    onBlur: () => {
      setEditing(false);
      setBlurred(true);
    },
    onEdit: () => setEditing(true),
  };
}
